import React from "react";
import { MapPin, Truck, PawPrint } from "lucide-react";

interface PricingDetails {
  distanceKm?: number;
  baseFare?: number;
  additionalDistanceKm?: number;
  additionalCharge?: number;
  totalPrice?: number;
  dubaiBaseFare?: number;
  sharjahBaseFare?: number;
  abuDhabiBaseFare?: number;
  includedKm?: number;
  extraKmRate?: number;
  freeWaitingMinutes?: number;
}

interface TaxiSummaryCardProps {
  petName?: string;
  petType?: string;
  breed?: string;
  weight?: string | number;
  pickupCity?: string;
  dropCity?: string;
  pickupAddress?: string;
  dropAddress?: string;
  pricing?: PricingDetails; // API Pricing Response Object
  distanceKm?: number;
  baseFare?: number;
  additionalDistanceKm?: number;
  additionalCharge?: number;
  totalPrice?: number;
}

const TaxiSummaryCard: React.FC<TaxiSummaryCardProps> = ({
  petName,
  petType,
  breed,
  weight,
  pickupCity,
  dropCity,
  pickupAddress,
  dropAddress,
  pricing,
  distanceKm: directDistanceKm,
  baseFare: directBaseFare,
  additionalDistanceKm: directAdditionalDistanceKm,
  additionalCharge: directAdditionalCharge,
  totalPrice: directTotalPrice,
}) => {
  // Extract values from pricing object with dynamic fallbacks
  const distanceKm = pricing?.distanceKm ?? directDistanceKm ?? 0;
  const includedKm = pricing?.includedKm ?? 15;
  const baseFare = pricing?.baseFare ?? directBaseFare ?? 80;
  const extraKm =
    pricing?.additionalDistanceKm ??
    directAdditionalDistanceKm ??
    Math.max(distanceKm - includedKm, 0);
  const additionalCharge =
    pricing?.additionalCharge ?? directAdditionalCharge ?? 0;
  const estimatedFare = pricing?.totalPrice ?? directTotalPrice ?? (baseFare + additionalCharge);

  // Dynamic pricing rule notes variables
  const dubaiBaseFare = pricing?.dubaiBaseFare ?? 100;
  const sharjahBaseFare = pricing?.sharjahBaseFare ?? 110;
  const abuDhabiBaseFare = pricing?.abuDhabiBaseFare ?? 140;
  const extraKmRate = pricing?.extraKmRate ?? 6;
  const freeWaitingMinutes = pricing?.freeWaitingMinutes ?? 10;

  return (
    <div className="bg-white rounded-[2rem] border border-[#ECE7DF] shadow-sm p-6 space-y-5 text-left">
      <div>
        <h2 className="text-xl font-bold text-logoHeading">
          Taxi Booking Summary
        </h2>
        <p className="text-sm text-gray-500">
          Estimated transportation details
        </p>
      </div>

      {/* Pet Details */}
      <div className="rounded-xl bg-gray-50 px-4 py-3">
        <div className="flex items-center gap-2 mb-1.5">
          <PawPrint size={16} className="text-iconColor" />
          <span className="text-base font-semibold text-iconColor">
            Pet Details
          </span>
        </div>

        {petName && (
          <p className="text-sm font-bold text-gray-900">{petName}</p>
        )}

        <p className="text-sm text-gray-700">
          {[breed, petType].filter(Boolean).join(" • ") || "Standard Pet"}
        </p>

        {weight && (
          <p className="text-xs text-gray-500 mt-0.5">
            Weight/Size: <span className="font-semibold text-gray-700">{weight}</span>
          </p>
        )}
      </div>

      {/* Pickup Location */}
      <div className="rounded-xl bg-gray-50 px-4 py-3">
        <div className="flex items-center gap-2 mb-1.5">
          <MapPin size={16} className="text-[#6DBE45]" />
          <span className="text-base font-semibold text-iconColor">
            Pickup
          </span>
        </div>

        <p className="text-sm text-gray-800 font-medium">
          {pickupAddress || "Pickup location set"}
        </p>
        {pickupCity && (
          <p className="text-xs text-gray-500 mt-0.5">{pickupCity}</p>
        )}
      </div>

      {/* Dropoff Location */}
      <div className="rounded-xl bg-gray-50 px-4 py-3">
        <div className="flex items-center gap-2 mb-1.5">
          <Truck size={16} className="text-red-500" />
          <span className="text-base font-semibold text-iconColor">Drop</span>
        </div>

        <p className="text-sm text-gray-800 font-medium">
          {dropAddress || "Destination location set"}
        </p>
        {dropCity && (
          <p className="text-xs text-gray-500 mt-0.5">{dropCity}</p>
        )}
      </div>

      {/* Fare Breakdown */}
        {/* Fare Breakdown */}
      <div className="bg-emerald-50/60 rounded-2xl p-5 border border-emerald-200/80 shadow-sm">
        <div className="flex items-center gap-2 text-emerald-800 font-extrabold text-base mb-4">
          <Truck size={19} className="text-[#6DBE45]" />
          <span>Estimated Taxi Fare Breakdown</span>
        </div>

        <div className="space-y-3 text-sm">

          {/* Total Route Distance */}
          <div className="flex justify-between items-center">
            <span className="text-gray-600">
              Total Route Distance
            </span>
            <span className="font-bold text-gray-900">
              {Number(distanceKm).toFixed(2)} KM
            </span>
          </div>

          {/* Base Fare */}
          <div className="flex justify-between items-center">
            <span className="text-gray-600">
              Base Fare
            </span>
            <span className="font-bold text-gray-900">
              AED {Number(baseFare).toFixed(2)}
            </span>
          </div>

          {/* Fare Information */}
          <div className="mt-3 rounded-xl bg-white/80 border border-emerald-100 p-4">

            <div className="text-[11px] font-extrabold text-emerald-800 uppercase tracking-wide mb-3">
              Fare Information
            </div>

            <div className="space-y-2.5 text-xs">

              {/* Included KM */}
              <div className="flex justify-between">
                <span className="text-gray-600">
                  Included Distance
                </span>
                <span className="font-bold text-gray-800">
                  {Number(includedKm).toFixed(0)} KM
                </span>
              </div>

              {/* Extra KM Rate */}
              <div className="flex justify-between">
                <span className="text-gray-600">
                  Extra Distance Rate
                </span>
                <span className="font-bold text-gray-800">
                  AED {Number(extraKmRate).toFixed(2)} / KM
                </span>
              </div>

              {/* Additional Distance */}
              <div className="flex justify-between">
                <span className="text-gray-600">
                  Additional Distance
                </span>
                <span className="font-bold text-gray-800">
                  {Number(extraKm).toFixed(2)} KM
                </span>
              </div>

              {/* Additional Charge */}
              <div className="flex justify-between">
                <span className="text-gray-600">
                  Additional Distance Charge
                </span>
                <span className="font-bold text-gray-800">
                  AED {Number(additionalCharge).toFixed(2)}
                </span>
              </div>

              {/* Waiting */}
              <div className="flex justify-between">
                <span className="text-gray-600">
                  Free Waiting Time
                </span>
                <span className="font-bold text-gray-800">
                  {Number(freeWaitingMinutes).toFixed(0)} minutes
                </span>
              </div>
            </div>
          </div>

          {/* Emirate Base Fares */}
          <div className="mt-2 rounded-xl bg-white/80 border border-emerald-100 p-4">

            <div className="text-[11px] font-extrabold text-emerald-800 uppercase tracking-wide mb-3">
              Emirate Base Fares
            </div>

            <div className="space-y-2.5 text-xs">

              <div className="flex justify-between">
                <span className="text-gray-600">
                  Dubai
                </span>
                <span className="font-bold text-gray-800">
                  AED {Number(dubaiBaseFare).toFixed(2)}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-600">
                  Sharjah
                </span>
                <span className="font-bold text-gray-800">
                  AED {Number(sharjahBaseFare).toFixed(2)}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-600">
                  Abu Dhabi
                </span>
                <span className="font-bold text-gray-800">
                  AED {Number(abuDhabiBaseFare).toFixed(2)}
                </span>
              </div>

            </div>
          </div>

          {/* Estimated Fare */}
          <div className="flex justify-between items-center text-base font-black pt-3 mt-2 border-t border-emerald-200">
            <span className="text-logoHeading">
              Estimated Fare
            </span>

            <span className="text-[#2F5E2E]">
              AED {Number(estimatedFare).toFixed(2)}
            </span>
          </div>

        </div>
      </div>
    </div>
  );
};

export default TaxiSummaryCard;